import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import DashboardIcon from "@mui/icons-material/SpaceDashboardRounded";
import MoreIcon from "@mui/icons-material/MoreVertRounded";
import ChevronIcon from "@mui/icons-material/ChevronRightRounded";
import RadarIcon from "@mui/icons-material/RadarRounded";
import LinkIcon from "@mui/icons-material/LinkRounded";
import MenuIcon from "@mui/icons-material/MenuRounded";

import type { BotMode, BrokerInfo } from "../api";
import { PROFILE } from "../profile";

interface TopBarProps {
  /** Opens the phone nav drawer. Absent on desktop, where the rail is fixed. */
  onMenu?: () => void;
  title: string;
  mode: BotMode;
  scans: number;
  clock: string;
  broker?: BrokerInfo;
}

export default function TopBar({ onMenu, title, mode, scans, clock, broker }: TopBarProps) {
  const isLive = mode === "LIVE";
  const offline = broker?.offline === true;
  // "paper" is the honest word until an adapter exposes an order gateway --
  // and on a frozen feed even that overstates it: there is no market to fill
  // against, paper or otherwise.
  const execution = offline
    ? "no live market"
    : broker?.live_orders
      ? "live orders"
      : "paper fills";
  // A scan counter on a frozen feed measures how many times the same bar was
  // re-read. It looks exactly like activity and is the opposite of it, so the
  // last real bar is shown in its place.
  const lastBar = broker?.last_bar
    ? new Date(broker.last_bar).toLocaleString("en-IN", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      })
    : null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={2}
      sx={{
        px: { xs: 1.5, md: 3 },
        py: 1.5,
        position: "sticky",
        top: 0,
        zIndex: 5,
        borderBottom: "1px solid var(--border)",
        background: "rgba(10,14,20,.72)",
        backdropFilter: "blur(14px)",
      }}
    >
      {/* Phone-only nav trigger. The 262px rail is hidden below lg, so without
          this there is no way to reach any other page on a handset. */}
      <IconButton
        onClick={onMenu}
        aria-label="Open navigation"
        sx={{ display: { xs: "inline-flex", lg: "none" }, ml: -0.5, mr: -0.5,
              color: "var(--text)" }}
      >
        <MenuIcon />
      </IconButton>

      <Stack direction="row" alignItems="center" gap={1.3} sx={{ minWidth: 0 }}>
        <DashboardIcon sx={{ fontSize: 21, color: "var(--muted)",
                             display: { xs: "none", sm: "block" } }} />
        <Typography noWrap sx={{ fontSize: { xs: ".9rem", md: "1.02rem" },
                                 fontWeight: 700 }}>
          {title}
        </Typography>
      </Stack>

      {/* connection / engine status chip */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        className="panel3d"
        sx={{ ml: "auto", px: { xs: 1, md: 2 }, py: 1, flexShrink: 0 }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "9px",
            display: "grid",
            placeItems: "center",
            background: isLive
              ? "linear-gradient(145deg,#0f7a45,#064d29)"
              : "linear-gradient(145deg,#2a3444,#171f2b)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,.22)",
          }}
        >
          <RadarIcon sx={{ fontSize: 18, color: isLive ? "#7fffc4" : "#7b8ea3" }} />
        </Box>

        <Box sx={{ lineHeight: 1.35, display: { xs: "none", md: "block" } }}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Typography sx={{ fontSize: ".82rem", fontWeight: 700 }}>
              {broker?.name ?? "No broker"}
            </Typography>
            <Typography
              className="mono"
              sx={{
                fontSize: ".82rem",
                fontWeight: 800,
                color: offline ? "var(--amber, #d9a441)" : "var(--text-2)",
              }}
              title={offline ? broker?.note : undefined}
            >
              {offline
                ? `frozen${lastBar ? ` · ${lastBar}` : ""}`
                : `${scans.toLocaleString("en-IN")} scans`}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1.2}>
            <Typography sx={{ fontSize: ".7rem", color: "var(--muted)" }}>
              {execution} · IST{" "}
              <span className="mono" style={{ color: "var(--blue)" }}>
                {clock}
              </span>
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.6}>
              <span className={isLive ? "dot-live" : "dot-off"} />
              <Typography
                sx={{
                  fontSize: ".7rem",
                  fontWeight: 700,
                  color: isLive ? "var(--green)" : "var(--muted)",
                }}
              >
                {isLive ? "Scanning" : "Paused"}
              </Typography>
              <LinkIcon sx={{ fontSize: 13, color: "var(--muted)" }} />
            </Stack>
          </Stack>
        </Box>

        <Tooltip title="Engine options">
          <MoreIcon
            sx={{ fontSize: 19, color: "var(--muted)", cursor: "pointer",
                  display: { xs: "none", md: "block" } }}
          />
        </Tooltip>
      </Stack>

      {/* user chip */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1.2}
        className="panel3d"
        sx={{ px: 1.5, py: 0.9, cursor: "pointer",
              display: { xs: "none", md: "flex" } }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            fontSize: ".82rem",
            fontWeight: 800,
            color: "#04121f",
            background: "linear-gradient(145deg,#7fe3a6,#00e676)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,.5)",
          }}
        >
          {PROFILE.initial}
        </Box>
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography sx={{ fontSize: ".84rem", fontWeight: 700 }}>
            {PROFILE.name}
          </Typography>
          <Typography sx={{ fontSize: ".68rem", color: "var(--muted)" }}>
            {PROFILE.handle}
          </Typography>
        </Box>
        <ChevronIcon sx={{ fontSize: 19, color: "var(--muted)" }} />
      </Stack>
    </Stack>
  );
}
